"use client";

/**
 * Arch radius morph (Venetian recipe 3).
 *
 * Section rendered with a giant top arch. On desktop the radius starts at
 * 1250px and GSAP scrubs it down to 400px between "top 95%" and "+=600":
 * the arch flattens as it rises into view. Tablet and mobile hold static
 * radii (300px / 150px), matching the Venetian source.
 *
 * The static CSS classes double as the no-JS and reduced-motion state
 * (desktop rests at the 400px end value), so content never depends on
 * the animation.
 */
import { useRef, type HTMLAttributes } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

export type ArchSectionProps = HTMLAttributes<HTMLElement>;

export function ArchSection({
  children,
  className = "",
  ...rest
}: ArchSectionProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      if (prefersReducedMotion()) return;
      const mm = gsap.matchMedia();
      mm.add("(min-width: 1024px)", () => {
        gsap.fromTo(
          ref.current,
          { borderTopLeftRadius: 1250, borderTopRightRadius: 1250 },
          {
            borderTopLeftRadius: 400,
            borderTopRightRadius: 400,
            ease: "none",
            scrollTrigger: {
              trigger: ref.current,
              start: "top 95%",
              end: "+=600",
              scrub: true,
            },
          }
        );
      });
    },
    { scope: ref }
  );

  return (
    <section
      ref={ref}
      // `overflow-hidden` is deliberate and load-bearing: at tablet and mobile
      // widths the photography in these sections reaches the section edge, and
      // the arch curve cutting across it is the intended look (owner, 2026-08-04).
      //
      // It costs something, though. The same clip also lands on text, and the
      // corner curve was slicing the first characters off headings and labels
      // on the five call sites that had no top padding of their own. The fix is
      // clearance, not the removal of the clip: content near the top of an
      // ArchSection must start below the steep part of the curve.
      //
      // ARCH_CONTENT_CLEARANCE (archClearance.ts) is that clearance, named and
      // derived, so a new call site inherits it instead of rediscovering the
      // problem. TrustBar and /pricing had already solved it by hand.
      className={`overflow-hidden rounded-t-[150px] md:rounded-t-[300px] lg:rounded-t-[400px] ${className}`}
      {...rest}
    >
      {children}
    </section>
  );
}
